import React, { useState, useEffect, useRef } from "react";
import { 
  FaCamera, FaSearch, FaUserCheck, FaTimes, FaSpinner, 
  FaCheckCircle, FaExclamationTriangle, FaRedo, FaVideo, FaVideoSlash,
  FaLock, FaLockOpen, FaAward, FaCalendarCheck, FaFingerprint
} from "react-icons/fa";
import toast from "react-hot-toast";
import { getAllCandidates, updateCandidate } from "../../Api/candidateApi";
import { getAllProjects } from "../../Api/projectApi";
import { logClientAction } from "../../Api/logApi";

const safeLogClientAction = (action, module, metadata = {}) => {
  logClientAction(action, module, metadata).catch((err) => {
    console.error("Action Log Error:", err);
  });
};

const FaceVerification = () => {
  const role = localStorage.getItem("role");
  const [candidates, setCandidates] = useState([]);
  const [projectsList, setProjectsList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [stream, setStream] = useState(null);
  const [enrolledImage, setEnrolledImage] = useState(null);
  const [currentSnapshot, setCurrentSnapshot] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);  
  const [verificationResult, setVerificationResult] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [modelsLoaded, setModelsLoaded] = useState(false);
  
  // Attendance and Duration states
  const [courseDurationDays, setCourseDurationDays] = useState(30); // Default fallback
  const [attendanceRecords, setAttendanceRecords] = useState([]);  // List of checked-in day numbers (e.g., [1, 2])
  const [targetDayToVerify, setTargetDayToVerify] = useState(1);   // The day currently selected for verification

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const streamRef = useRef(null);

  // Fetch candidates and projects list on load/search
  useEffect(() => {
    fetchCandidates();
  }, [searchQuery]);

  // Load FaceAPI models on mount
  useEffect(() => {
    const loadModels = async () => {
      try {
        if (window.faceapi) {
          // Load SSD MobileNet face detector, landmarks, and face recognition models
          await window.faceapi.nets.ssdMobilenetv1.loadFromUri('https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@master/weights');
          await window.faceapi.nets.faceLandmark68Net.loadFromUri('https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@master/weights');
          await window.faceapi.nets.faceRecognitionNet.loadFromUri('https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@master/weights');
          setModelsLoaded(true);
          console.log("FaceAPI models loaded successfully");
        }
      } catch (err) {
        console.error("Failed to load FaceAPI models:", err);
        toast.error("Failed to initialize face recognition engine.");
      }
    };
    loadModels();
  }, []);

  useEffect(() => {
    loadProjects();
  }, []);

  // Fetch list of camera devices
  useEffect(() => {
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      navigator.mediaDevices.enumerateDevices()
        .then(deviceList => {
          const videoDevices = deviceList.filter(d => d.kind === "videoinput");
          setDevices(videoDevices);
          if (videoDevices.length > 0) {
            setSelectedDevice(videoDevices[0].deviceId);
          }
        })
        .catch(err => console.error("Error listing cameras:", err));
    }
  }, []);

  const loadProjects = async () => {
    try {
      const res = await getAllProjects();
      if (res && res.success && Array.isArray(res.data)) {
        setProjectsList(res.data);
      } else if (Array.isArray(res)) {
        setProjectsList(res);
      }
    } catch (err) {
      console.error("Failed to load projects mapping:", err);
    }
  };

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const res = await getAllCandidates(1, 100, "", searchQuery);
      if (res.data?.success) {
        setCandidates(res.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch candidates:", err);
      toast.error("Failed to load candidate list");
    } finally {
      setLoading(false);
    }
  };

  // Helper to parse course duration string into days
  const parseDurationToDays = (durationStr) => {
    if (!durationStr) return 30; // default fallback
    const clean = durationStr.toLowerCase();
    const num = parseInt(clean.replace(/\D/g, ""), 10);
    if (isNaN(num)) {
      const words = { "one": 1, "two": 2, "three": 3, "four": 4, "five": 5, "ten": 10 };
      for (const w in words) {
        if (clean.includes(w)) return words[w];
      }
      return 30;
    }

    if (clean.includes("month")) {
      return num * 30; // convert months to days
    }
    if (clean.includes("week")) {
      return num * 7; // convert weeks to days
    }
    return num; // assume days
  };

  const parseDurationFromText = (text) => {
    if (!text) return null;
    const clean = text.toLowerCase();
    
    const numberWords = {
      "one": 1, "two": 2, "three": 3, "four": 4, "five": 5, 
      "six": 6, "seven": 7, "eight": 8, "nine": 9, "ten": 10,
      "fifteen": 15, "thirty": 30, "forty five": 45, "forty-five": 45,
      "ninety": 90
    };
    
    for (const word in numberWords) {
      if (clean.includes(`${word} day`) || clean.includes(`${word}-day`) || clean.includes(word)) {
        return numberWords[word];
      }
    }
    
    const match = clean.match(/(\d+)/);
    if (match) {
      return parseInt(match[1], 10);
    }
    
    return null;
  };

  // Helper to check if candidate is already enrolled
  const checkEnrollmentStatus = (candidateId) => {
    const cand = candidates.find(x => x._id === candidateId);
    if (cand && cand.biometricFace) return true;
    return !!localStorage.getItem(`dbskills_face_${candidateId}`);
  };

  // Select a candidate
  const handleSelectCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    
    // Load enrolled image from candidate object, fallback to localStorage
    const savedFace = candidate.biometricFace || localStorage.getItem(`dbskills_face_${candidate._id}`);
    setEnrolledImage(savedFace || null);
    
    // Load attendance records from candidate object, fallback to localStorage
    const serverAttendance = candidate.attendanceLogs || [];
    const attendanceKey = `dbskills_attendance_${candidate._id}`;
    const localAttendance = JSON.parse(localStorage.getItem(attendanceKey) || "[]");
    
    // Merge them by matching the day property to avoid duplicates
    const combined = [...serverAttendance];
    localAttendance.forEach(item => {
      const dayNum = typeof item === 'number' ? item : item.day;
      if (!combined.some(r => r.day === dayNum)) {
        combined.push(typeof item === 'number' ? { day: item, verifiedAt: null } : item);
      }
    });

    const formattedRecords = combined.map(item => {
      if (typeof item === 'number') {
        return { day: item, verifiedAt: null };
      }
      return item;
    });
    setAttendanceRecords(formattedRecords);

    // Calculate next day to check-in
    let nextDay = 1;
    if (formattedRecords.length > 0) {
      const days = formattedRecords.map(r => r.day);
      const maxCheckedDay = Math.max(...days);
      nextDay = maxCheckedDay + 1;
    }
    setTargetDayToVerify(nextDay);

    // Determine course duration dynamically based on candidate's project program
    let durationVal = 30; // default fallback
    if (projectsList.length > 0 && candidate.project) {
      const matched = projectsList.find(
        (p) => 
          p.projectName.trim().toLowerCase() === candidate.project.trim().toLowerCase() &&
          p.programName.trim().toLowerCase() === (candidate.program || "").trim().toLowerCase()
      );
      if (matched && matched.courseDuration) {
        durationVal = parseDurationToDays(matched.courseDuration);
      } else {
        const matchedProg = projectsList.find(
          (p) => p.programName.trim().toLowerCase() === (candidate.program || "").trim().toLowerCase()
        );
        if (matchedProg && matchedProg.courseDuration) {
          durationVal = parseDurationToDays(matchedProg.courseDuration);
        }
      }
    }

    if (durationVal === 30 && candidate.program) {
      const textParsed = parseDurationFromText(candidate.program);
      if (textParsed !== null) {
        durationVal = textParsed;
      }
    }

    if (durationVal === 30 && candidate.project) {
      const textParsed = parseDurationFromText(candidate.project);
      if (textParsed !== null) {
        durationVal = textParsed;
      }
    }

    setCourseDurationDays(durationVal);

    // Reset verification states
    setVerificationResult(null);
    setCurrentSnapshot(null);
    stopCamera();
  };

  // Start WebRTC Camera stream
  const startCamera = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error("Webcam access is blocked on unsecure HTTP connections. Please use localhost or HTTPS.");
      return;
    }

    if (stream) stopCamera();

    const constraints = {
      video: (selectedDevice && selectedDevice !== "") 
        ? { deviceId: { exact: selectedDevice } } 
        : true
    };

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = mediaStream;
      setStream(mediaStream);
      setCameraActive(true);

      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.play().catch(e => console.error("Play error:", e));
        }
      }, 150);

      toast.success("Camera connected");

      // Query devices list again to populate labels and real IDs now that permission is granted
      if (navigator.mediaDevices.enumerateDevices) {
        navigator.mediaDevices.enumerateDevices()
          .then(deviceList => {
            const videoDevices = deviceList.filter(d => d.kind === "videoinput");
            setDevices(videoDevices);
            if (videoDevices.length > 0 && !selectedDevice) {
              setSelectedDevice(videoDevices[0].deviceId);
            }
          })
          .catch(err => console.warn("Error enumeration updating:", err));
      }
    } catch (err) {
      console.error("Camera access error:", err);
      toast.error("Could not access camera. Please check permissions.");
    }
  };

  // Stop WebRTC Camera stream
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setStream(null);
    setCameraActive(false);
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  };

  // Auto-start camera on mount and clean up on unmount
  useEffect(() => {
    startCamera();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // Run beautiful canvas scan overlay animations
  const runScanAnimation = (canvas, ctx, isEnrollment, snapshotBase64) => {
    let progress = 0;
    let laserY = 0;
    let laserDirection = 1;

    // Define mock face landmark coordinates relative to canvas center
    const faceLandmarks = [
      { x: 0.5, y: 0.4 }, // Left Eye
      { x: 0.62, y: 0.4 }, // Right Eye
      { x: 0.56, y: 0.52 }, // Nose
      { x: 0.56, y: 0.65 }, // Mouth Center
      { x: 0.48, y: 0.64 }, // Mouth Left
      { x: 0.64, y: 0.64 }, // Mouth Right
      { x: 0.38, y: 0.45 }, // Left cheek
      { x: 0.74, y: 0.45 }, // Right cheek
      { x: 0.56, y: 0.8 }, // Chin
    ];

    const animate = () => {
      if (!canvas || !ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const w = canvas.width;
      const h = canvas.height;

      // 1. Draw Scanner Oval/Frame Guide
      ctx.strokeStyle = "rgba(43, 181, 206, 0.4)";
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 10]);
      ctx.beginPath();
      ctx.ellipse(w / 2, h / 2, w * 0.28, h * 0.38, 0, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.setLineDash([]);

      // 2. Draw Laser Sweep line
      ctx.strokeStyle = "#2BB5CE";
      ctx.lineWidth = 3;
      ctx.shadowBlur = 15;
      ctx.shadowColor = "#2BB5CE";
      ctx.beginPath();
      ctx.moveTo(w / 2 - w * 0.28, laserY);
      ctx.lineTo(w / 2 + w * 0.28, laserY);
      ctx.stroke();
      ctx.shadowBlur = 0; // reset shadow

      // Update laser position
      laserY += 5 * laserDirection;
      const topBound = h / 2 - h * 0.38;
      const bottomBound = h / 2 + h * 0.38;
      if (laserY > bottomBound) {
        laserY = bottomBound;
        laserDirection = -1;
      } else if (laserY < topBound) {
        laserY = topBound;
        laserDirection = 1;
      }

      // 3. Draw Biometric Landmarking Dots
      progress += 15.0;
      setScanProgress(Math.min(Math.round(progress), 100));

      const visibleDotsCount = Math.floor((progress / 100) * faceLandmarks.length);
      for (let i = 0; i < visibleDotsCount; i++) {
        const pt = faceLandmarks[i];
        const ptX = pt.x * w;
        const ptY = pt.y * h;

        ctx.fillStyle = "#10B981";
        ctx.beginPath();
        ctx.arc(ptX, ptY, 4, 0, 2 * Math.PI);
        ctx.fill();

        ctx.strokeStyle = "rgba(16, 185, 129, 0.25)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(w / 2, h / 2);
        ctx.lineTo(ptX, ptY);
        ctx.stroke();
      }

      // 4. Draw corner indicators
      const cornerSize = 25;
      ctx.strokeStyle = "#2BB5CE";
      ctx.lineWidth = 4;

      ctx.beginPath();
      ctx.moveTo(w * 0.2, h * 0.2 + cornerSize);
      ctx.lineTo(w * 0.2, h * 0.2);
      ctx.lineTo(w * 0.2 + cornerSize, h * 0.2);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(w * 0.8 - cornerSize, h * 0.2);
      ctx.lineTo(w * 0.8, h * 0.2);
      ctx.lineTo(w * 0.8, h * 0.2 + cornerSize);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(w * 0.2, h * 0.8 - cornerSize);
      ctx.lineTo(w * 0.2, h * 0.8);
      ctx.lineTo(w * 0.2 + cornerSize, h * 0.8);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(w * 0.8 - cornerSize, h * 0.8);
      ctx.lineTo(w * 0.8, h * 0.8);
      ctx.lineTo(w * 0.8, h * 0.8 - cornerSize);
      ctx.stroke();

      if (progress < 100) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        finishScanning(isEnrollment, snapshotBase64);
      }
    };

    laserY = canvas.height / 2 - canvas.height * 0.38;
    animate();
  };

  // Trigger capture & start scan animation
  const triggerScan = (isEnrollment) => {
    if (!videoRef.current || !stream) {
      toast.error("Webcam stream is not active");
      return;
    }

    setIsScanning(true);
    setScanProgress(0);
    setVerificationResult(null);

    // Prepare offscreen canvas snapshot
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const snapshotBase64 = canvas.toDataURL("image/jpeg");
    setCurrentSnapshot(snapshotBase64);

    // Now start drawing scan animations over the top
    runScanAnimation(canvas, ctx, isEnrollment, snapshotBase64);
  };

  // Genuine canvas-based pixel similarity matching
  const loadFaceApiModels = async () => {
    if (!window.faceapi) return false;
    const nets = window.faceapi.nets;
    if (!nets.ssdMobilenetv1.isLoaded || !nets.faceLandmark68Net.isLoaded || !nets.faceRecognitionNet.isLoaded) {
      console.log("Loading FaceAPI models...");
      await nets.ssdMobilenetv1.loadFromUri('https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@master/weights');
      await nets.faceLandmark68Net.loadFromUri('https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@master/weights');
      await nets.faceRecognitionNet.loadFromUri('https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@master/weights');
      console.log("FaceAPI models loaded successfully");
    }
    return true;
  };

  const getFaceDescriptor = async (imageSrc) => {
    if (!window.faceapi) {
      toast.error("Biometric engine script is still loading. Please wait a moment.");
      return null;
    }
    
    try {
      await loadFaceApiModels();
    } catch (err) {
      console.error("Error loading models:", err);
      toast.error("Failed to load biometric model files.");
      return null;
    }
    
    const img = new Image();
    img.src = imageSrc;
    await new Promise((resolve) => {
      img.onload = resolve;
    });

    try {
      const detection = await window.faceapi
        .detectSingleFace(img, new window.faceapi.SsdMobilenetv1Options({ minConfidence: 0.65 }))
        .withFaceLandmarks()
        .withFaceDescriptor();
      
      return detection ? detection.descriptor : null;
    } catch (err) {
      console.error("FaceAPI extraction error:", err);
      return null;
    }
  };

  // Genuine neural network-based face comparison
  const calculatePixelSimilarity = async (imgUrl1, imgUrl2) => {
    const desc1 = await getFaceDescriptor(imgUrl1);
    const desc2 = await getFaceDescriptor(imgUrl2);

    if (!desc1 || !desc2) {
      return 0; // failed to detect face
    }

    const distance = window.faceapi.euclideanDistance(desc1, desc2);
    const similarity = Math.max(0, Math.min(100, 100 - (distance / 0.5) * 30));
    return similarity.toFixed(1);
  };

  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  // Scan finished logic
  const finishScanning = async (isEnrollment, snapshotBase64) => {
    setIsScanning(false);
    safeLogClientAction("BIOMETRIC_STARTED", "Biometric");

    const OCR_API_URL = process.env.REACT_APP_OCR_API_URL || "https://ocr.eduforestin.com";

    // Convert base64 to File object
    let imageFile;
    try {
      imageFile = dataURLtoFile(snapshotBase64, "face.jpg");
    } catch (e) {
      console.error("Failed to convert base64 snapshot to file:", e);
      toast.error("Failed to process snapshot image.");
      stopCamera();
      return;
    }

    if (isEnrollment) {
      if (!selectedCandidate) {
        toast.error("Please select a candidate to enroll first.");
        stopCamera();
        safeLogClientAction("BIOMETRIC_FAILED", "Biometric", { success: false, error: "No candidate selected" });
        return;
      }
      
      const toastId = toast.loading("Processing biometric signature...");

      try {
        const ocrFormData = new FormData();
        ocrFormData.append("candidateId", selectedCandidate._id);
        ocrFormData.append("image", imageFile);

        const response = await fetch(`${OCR_API_URL}/v1/face/enroll`, {
          method: "POST",
          headers: {
            "x-internal-key": "dev-ocr-internal-key",
          },
          body: ocrFormData,
        });

        if (!response.ok) {
          throw new Error(`Enrollment server returned status ${response.status}`);
        }

        const result = await response.json();
        
        if (!result.success) {
          throw new Error(result.message || "Face extraction failed");
        }

        // Store the snapshot locally as a visual reference
        localStorage.setItem(`dbskills_face_${selectedCandidate._id}`, snapshotBase64);
        setEnrolledImage(snapshotBase64);

        toast.success("Biometric Face Template Registered!", { id: toastId });
        safeLogClientAction("BIOMETRIC_COMPLETED", "Biometric", { success: true, candidateId: selectedCandidate._id, mode: "Enrollment" });
      } catch (err) {
        console.error("Failed to enroll face signature:", err);
        toast.error(`Enrollment Failed: ${err.message}`, { id: toastId, duration: 6000 });
        safeLogClientAction("BIOMETRIC_FAILED", "Biometric", { success: false, error: err.message, candidateId: selectedCandidate._id, mode: "Enrollment" });
      }

      stopCamera();
      fetchCandidates();
      return;
    }

    // Biometric Verification / Identification Flow
    const toastId = toast.loading("Analyzing biometric features...");

    try {
      const ocrFormData = new FormData();
      if (selectedCandidate) {
        ocrFormData.append("candidateId", selectedCandidate._id);
      }
      ocrFormData.append("image", imageFile);

      const response = await fetch(`${OCR_API_URL}/v1/face/verify`, {
        method: "POST",
        headers: {
          "x-internal-key": "dev-ocr-internal-key",
        },
        body: ocrFormData,
      });

      if (!response.ok) {
        throw new Error(`Verification server returned status ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success || !result.matched) {
        // Verification failed
        const failMsg = result.message || "Face does not match any registered candidate.";
        toast.error(`Verification Failed: ${failMsg}`, { id: toastId, duration: 5000 });
        setVerificationResult({
          success: false,
          score: result.score || 0,
          message: failMsg
        });
        safeLogClientAction("BIOMETRIC_FAILED", "Biometric", { success: false, error: failMsg });
        stopCamera();
        return;
      }

      // If matched, resolve the candidate object
      let matchedCandidate = selectedCandidate;
      if (!matchedCandidate && result.candidateId) {
        matchedCandidate = candidates.find(c => c._id === result.candidateId);
      }

      if (!matchedCandidate) {
        throw new Error("Matched candidate not found in local candidates list.");
      }

      // Select candidate if it wasn't selected
      if (!selectedCandidate) {
        handleSelectCandidate(matchedCandidate);
      }

      const bestMatch = matchedCandidate;
      const maxScore = result.score || 100.0;

      // Load their attendance records
      const attendanceKey = `dbskills_attendance_${bestMatch._id}`;
      const savedAttendance = JSON.parse(localStorage.getItem(attendanceKey) || "[]");
      const formattedRecords = savedAttendance.map(item => {
        if (typeof item === 'number') {
          return { day: item, verifiedAt: null };
        }
        return item;
      });

      // Calculate project duration dynamically
      let durationVal = 30;
      if (projectsList.length > 0 && bestMatch.project) {
        const matched = projectsList.find(
          (p) => 
            p.projectName.trim().toLowerCase() === bestMatch.project.trim().toLowerCase() &&
            p.programName.trim().toLowerCase() === (bestMatch.program || "").trim().toLowerCase()
        );
        if (matched && matched.courseDuration) {
          durationVal = parseDurationToDays(matched.courseDuration);
        }
      }
      if (durationVal === 30 && bestMatch.program) {
        const textParsed = parseDurationFromText(bestMatch.program);
        if (textParsed !== null) durationVal = textParsed;
      }

      // Calculate next day to check-in
      let nextDay = 1;
      if (formattedRecords.length > 0) {
        const days = formattedRecords.map(r => r.day);
        const maxCheckedDay = Math.max(...days);
        nextDay = maxCheckedDay + 1;
      }

      if (nextDay > durationVal) {
        toast.success(`Access Approved: ${bestMatch.name} identified (${maxScore}% match), but all course days are verified.`, { id: toastId, duration: 5000 });
        setVerificationResult({
          success: true,
          score: maxScore,
          message: `Biometric match confirmed for ${bestMatch.name}. All course days verified.`
        });
        safeLogClientAction("BIOMETRIC_COMPLETED", "Biometric", { success: true, candidateId: bestMatch._id, score: maxScore, mode: "Verification", info: "All course days verified" });
      } else {
        const newRecord = { day: nextDay, verifiedAt: new Date().toISOString(), photo: snapshotBase64 };
        const updatedRecords = [...formattedRecords.filter(r => r.day !== nextDay), newRecord];
        localStorage.setItem(attendanceKey, JSON.stringify(updatedRecords));
        setAttendanceRecords(updatedRecords);
        setTargetDayToVerify(nextDay + 1);

        try {
          const formData = new FormData();
          formData.append("attendanceLogs", JSON.stringify(updatedRecords));
          await updateCandidate(bestMatch._id, formData);
          safeLogClientAction("BIOMETRIC_COMPLETED", "Biometric", { success: true, candidateId: bestMatch._id, score: maxScore, mode: "Verification", info: `Day ${nextDay} verified` });
        } catch (err) {
          console.error("Failed to save attendance check-in to database:", err);
          safeLogClientAction("BIOMETRIC_FAILED", "Biometric", { success: false, error: err.message, candidateId: bestMatch._id, score: maxScore, mode: "Verification" });
        }

        toast.success(`Attendance Approved: ${bestMatch.name} - Day ${nextDay} Verified! (${maxScore}% match)`, { id: toastId, duration: 5000 });
        setVerificationResult({
          success: true,
          score: maxScore,
          message: `Identified as ${bestMatch.name}. Day ${nextDay} attendance successfully verified.`
        });
        fetchCandidates();
      }

    } catch (err) {
      console.error("Failed to verify face:", err);
      toast.error(`Verification Failed: ${err.message}`, { id: toastId, duration: 5000 });
      setVerificationResult({
        success: false,
        score: 0,
        message: `Verification error: ${err.message}`
      });
      safeLogClientAction("BIOMETRIC_FAILED", "Biometric", { success: false, error: err.message });
    }

    stopCamera();
  };



  // Reset attendance records back to 0 check-ins
  const handleClearAttendance = async () => {
    if (window.confirm("Are you sure you want to reset all attendance logs for this candidate?")) {
      localStorage.removeItem(`dbskills_attendance_${selectedCandidate._id}`);
      setAttendanceRecords([]);
      setTargetDayToVerify(1);
      setVerificationResult(null);

      try {
        const formData = new FormData();
        formData.append("attendanceLogs", JSON.stringify([]));
        await updateCandidate(selectedCandidate._id, formData);
      } catch (err) {
        console.error("Failed to reset attendance in database:", err);
      }

      toast.success("Attendance history cleared successfully.");
      fetchCandidates();
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b pb-5" style={{ borderColor: "rgba(43,181,206,0.15)" }}>
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <FaFingerprint className="text-[#2BB5CE]" />
            Biometric Attendance Verification
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Log candidate attendance daily by verifying live camera scans against registered biometric face templates.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left Side: Candidate Selector list (Grid 4) */}
        <div className="lg:col-span-4 lg:order-2 flex flex-col space-y-4">
          <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-200 flex flex-col h-[650px]">
            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center justify-between">
              <span>Candidates</span>
              {loading && <FaSpinner className="animate-spin text-[#2BB5CE]" />}
            </h2>

            {/* Search Input */}
            <div className="relative mb-4">
              <input
                type="text"
                placeholder="Search name or Aadhaar..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl bg-gray-50 focus:ring-2 focus:ring-[#2BB5CE] focus:bg-white focus:outline-none transition-all text-sm"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <FaSearch className="absolute left-3.5 top-3 text-gray-400 text-sm" />
            </div>

            {/* Candidate List scrollbox */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-1">
              {candidates.length === 0 ? (
                <div className="text-center py-10 text-gray-400 text-sm">
                  {loading ? "Loading..." : "No candidates found."}
                </div>
              ) : (
                candidates.map((c) => {
                  const isEnrolled = checkEnrollmentStatus(c._id);
                  const isSelected = selectedCandidate?._id === c._id;
                  
                  // Calculate dynamic attendance stats
                  const attendanceKey = `dbskills_attendance_${c._id}`;
                  const localRecs = JSON.parse(localStorage.getItem(attendanceKey) || "[]");
                  const serverRecs = c.attendanceLogs || [];
                  const count = Math.max(localRecs.length, serverRecs.length);

                  return (
                    <div
                      key={c._id}
                      onClick={() => handleSelectCandidate(c)}
                      className={`p-3 rounded-xl border cursor-pointer transition-all flex items-center justify-between
                        ${isSelected 
                          ? "border-[#2BB5CE] bg-cyan-50/50 shadow-sm" 
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                        }`}
                    >
                      <div className="space-y-1 min-w-0 pr-2">
                        <p className="font-semibold text-gray-800 text-sm truncate">{c.name}</p>
                        <p className="text-[11px] text-gray-500 font-mono">Aadhaar: {c.aadharNumber || "N/A"}</p>
                        {count > 0 && (
                          <p className="text-[10px] text-emerald-600 font-semibold flex items-center gap-1">
                            <FaCalendarCheck className="text-[9px]" />
                            Checked In: {count} {count === 1 ? "day" : "days"}
                          </p>
                        )}
                      </div>
                      
                      {/* Status indicator */}
                      <div>
                        {isEnrolled ? (
                          <span className="px-2.5 py-1 text-[10px] font-semibold bg-emerald-100 text-emerald-700 rounded-full flex items-center gap-1 shadow-sm">
                            <FaUserCheck className="text-[10px]" />
                            Enrolled
                          </span>
                        ) : (
                          <span className="px-2.5 py-1 text-[10px] font-semibold bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
                            Missing Face
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Right Side: Camera Verifier Panel (Grid 8) */}
        <div className="lg:col-span-8 lg:order-1 space-y-6">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 flex flex-col justify-between space-y-6">
            
            {/* Header Box */}
            <div className="border-b pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" style={{ borderColor: "rgba(43,181,206,0.15)" }}>
              {selectedCandidate ? (
                <div>
                  <span className="text-[11px] font-bold tracking-wider text-[#2BB5CE] uppercase">Active Verification File</span>
                  <h2 className="text-xl font-bold text-gray-800">{selectedCandidate.name}</h2>
                  <p className="text-xs text-gray-500 flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5">
                    <span>Aadhaar: <strong className="font-mono text-gray-700">{selectedCandidate.aadharNumber || "N/A"}</strong></span>
                    <span>•</span>
                    <span>Project: <strong className="text-gray-700">{selectedCandidate.project || "N/A"}</strong></span>
                    <span>•</span>
                    <span>Program: <strong className="text-gray-700">{selectedCandidate.program || "N/A"}</strong></span>
                  </p>
                </div>
              ) : (
                <div>
                  <span className="text-[11px] font-bold tracking-wider text-blue-500 uppercase">Biometric Identification Terminal</span>
                  <h2 className="text-xl font-bold text-gray-800">Walk-up Attendance Clock</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Stand in front of the camera to verify your face and log today's attendance automatically.
                  </p>
                </div>
              )}
              
              {/* Reset Actions */}
              <div className="flex gap-2">
                {selectedCandidate && attendanceRecords.length > 0 && (
                  <button
                    onClick={handleClearAttendance}
                    className="px-3 py-1.5 border border-gray-200 text-gray-600 rounded-xl text-xs font-semibold hover:bg-gray-50 flex items-center gap-1.5 transition-all"
                  >
                    Reset Attendance
                  </button>
                )}

              </div>
            </div>

            {/* Main Scanner Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              
              {/* Left pane: Live Camera Stream Card */}
              <div className="flex flex-col items-center justify-center">
                <div className="relative w-full max-w-2xl aspect-video bg-gray-900 rounded-3xl overflow-hidden shadow-2xl border-4 border-gray-800 flex items-center justify-center">
                  
                  {/* Live Video */}
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className={`w-full h-full object-cover transform -scale-x-100 ${
                      cameraActive ? "block" : "hidden"
                    }`}
                  />

                  {/* Snapshot Canvas Overlay during scanning */}
                  <canvas
                    ref={canvasRef}
                    className={`w-full h-full object-cover transform -scale-x-100 absolute inset-0 pointer-events-none ${
                      isScanning ? "block" : "hidden"
                    }`}
                  />

                  {/* Idle state overlay */}
                  {!cameraActive && !isScanning && (
                    <div className="flex flex-col items-center text-center p-6 text-gray-400 space-y-3">
                      <div className="w-14 h-14 rounded-full bg-gray-800 flex items-center justify-center border border-gray-700">
                        <FaVideoSlash className="text-xl text-gray-500" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-300">Webcam Inactive</p>
                        <p className="text-xs text-gray-500 max-w-[200px] mt-1">Starting the webcam feed...</p>
                      </div>
                    </div>
                  )}

                  {/* High-tech Scanning animation label */}
                  {isScanning && (
                    <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                      <div className="w-32 h-32 border-2 border-dashed border-[#2BB5CE] rounded-full animate-pulse flex items-center justify-center">
                        <div className="w-24 h-24 border border-[#2BB5CE]/30 rounded-full animate-ping"></div>
                      </div>
                      <div className="w-full h-0.5 bg-[#2BB5CE] absolute top-0 left-0 animate-[bounce_2s_infinite] shadow-[0_0_10px_#2BB5CE]"></div>
                    </div>
                  )}

                  {/* Scanning Progress */}
                  {isScanning && (
                    <div className="absolute top-4 left-4 bg-cyan-900/85 backdrop-blur-md px-3 py-1 rounded-full border border-cyan-500/30 text-cyan-300 text-[10px] font-bold tracking-widest uppercase flex items-center gap-1.5 animate-pulse z-10">
                      <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-ping"></span>
                      Scanning Face... {scanProgress}%
                    </div>
                  )}
                </div>

                {/* Camera controls */}
                <div className="mt-4 w-full max-w-2xl flex items-center gap-2">
                  <select
                    className="flex-1 px-3 py-2 text-xs border border-gray-300 rounded-xl bg-white text-gray-600 focus:ring-1 focus:ring-[#2BB5CE] focus:outline-none"
                    value={selectedDevice}
                    onChange={(e) => {
                      setSelectedDevice(e.target.value);
                      if (cameraActive) startCamera();
                    }}
                    disabled={isScanning}
                  >
                    {devices.map((d, index) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label || `Camera ${index + 1}`}
                      </option>
                    ))}
                  </select>

                  <button
                    onClick={cameraActive ? stopCamera : startCamera}
                    disabled={isScanning}
                    className={`px-4 py-2 text-xs font-semibold rounded-xl flex items-center gap-1.5 transition-all
                      ${cameraActive 
                        ? "bg-gray-100 hover:bg-gray-200 text-gray-700" 
                        : "bg-[#2BB5CE] hover:bg-[#229fb5] text-white shadow-sm"
                      }`}
                  >
                    <FaVideo className="text-[11px]" />
                    {cameraActive ? "Turn Off" : "Turn On"}
                  </button>
                </div>
              </div>

              {/* Right pane: Biometric Comparison Panel */}
              <div className="flex flex-col justify-center h-full space-y-4">
                
                {/* Status Box */}
                <div className="bg-gray-50 border border-gray-150 p-4 rounded-2xl space-y-3">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {selectedCandidate ? "Enrolled Template Reference" : "Biometric Match Mode"}
                  </h3>

                  {selectedCandidate ? (
                    enrolledImage ? (
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-xl border border-gray-200 overflow-hidden bg-gray-100 shadow-inner flex-shrink-0">
                          <img
                            src={enrolledImage}
                            alt="Enrolled Profile Face"
                            className="w-full h-full object-cover transform -scale-x-100"
                          />
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-400 font-semibold uppercase">Enrollment Status</p>
                          <p className="text-xs font-bold text-emerald-600 flex items-center gap-1 mt-0.5">
                            <FaCheckCircle className="text-[10px]" />
                            Registered Template Found
                          </p>
                          <p className="text-[10px] text-gray-400 mt-0.5">Ready to verify</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-3 text-amber-600 bg-amber-50/50 p-3 rounded-xl border border-amber-150">
                        <FaExclamationTriangle className="text-base mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-bold uppercase">No Registration Face Print</p>
                          <p className="text-[10px] text-gray-500 mt-1 leading-normal">
                            Please click the capture button below to enroll this candidate's face print template first.
                          </p>
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="flex items-start gap-3 text-blue-600 bg-blue-50/50 p-3 rounded-xl border border-blue-150">
                      <FaFingerprint className="text-base mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-bold uppercase">Automatic Walk-up Recognition</p>
                        <p className="text-[10px] text-gray-500 mt-1 leading-normal">
                          The system will capture your face and match it against all enrolled templates in the database.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Result visualizer */}
                {verificationResult && (
                  <div className={`p-4 rounded-2xl border flex items-center gap-3 animate-fade-in
                    ${verificationResult.success 
                      ? "bg-emerald-50/40 border-emerald-200 text-emerald-800" 
                      : "bg-red-50/40 border-red-200 text-red-800"
                    }`}
                  >
                    <div className="text-2xl flex-shrink-0">
                      {verificationResult.success ? (
                        <FaCheckCircle className="text-emerald-500 animate-bounce" />
                      ) : (
                        <FaTimes className="text-red-500" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between">
                        <h4 className="text-xs font-bold truncate">
                          {verificationResult.success ? "Match Confirmed!" : "Verification Failed"}
                        </h4>
                        {verificationResult.score > 0 && (
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${verificationResult.success ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                            {verificationResult.score}% Similarity
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-gray-500 mt-1 leading-normal">
                        {verificationResult.message}
                      </p>
                    </div>
                  </div>
                )}

                {/* Verification Actions (Single Button!) */}
                <div>
                  {selectedCandidate ? (
                    enrolledImage ? (
                      <button
                        disabled={!cameraActive || isScanning || targetDayToVerify > courseDurationDays}
                        onClick={() => triggerScan(false)}
                        className={`w-full py-3.5 font-bold rounded-xl flex items-center justify-center gap-2.5 transition-all shadow-md text-sm
                          ${cameraActive && !isScanning && targetDayToVerify <= courseDurationDays
                            ? "bg-emerald-600 hover:bg-emerald-700 text-white hover:shadow-lg"
                            : "bg-gray-200 text-gray-400 cursor-not-allowed shadow-none"
                          }`}
                      >
                        {isScanning ? (
                          <>
                            <FaSpinner className="animate-spin text-sm" />
                            Matching Face...
                          </>
                        ) : targetDayToVerify > courseDurationDays ? (
                          "Course Verification Complete"
                        ) : (
                          <>
                            <FaUserCheck className="text-sm" />
                            Verify Day {targetDayToVerify} Attendance
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        disabled={!cameraActive || isScanning}
                        onClick={() => triggerScan(true)}
                        className={`w-full py-3.5 font-bold rounded-xl flex items-center justify-center gap-2.5 transition-all shadow-md text-sm
                          ${cameraActive && !isScanning
                            ? "bg-[#2BB5CE] hover:bg-[#229fb5] text-white hover:shadow-lg"
                            : "bg-gray-200 text-gray-400 cursor-not-allowed shadow-none"
                          }`}
                      >
                        {isScanning ? (
                          <>
                            <FaSpinner className="animate-spin text-sm" />
                            Registering Template...
                          </>
                        ) : (
                          <>
                            <FaCamera className="text-sm" />
                            Capture & Enroll Face Template
                          </>
                        )}
                      </button>
                    )
                  ) : (
                    <button
                      disabled={!cameraActive || isScanning}
                      onClick={() => triggerScan(false)}
                      className={`w-full py-3.5 font-bold rounded-xl flex items-center justify-center gap-2.5 transition-all shadow-md text-sm
                        ${cameraActive && !isScanning
                          ? "bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg"
                          : "bg-gray-200 text-gray-400 cursor-not-allowed shadow-none"
                        }`}
                    >
                      {isScanning ? (
                        <>
                          <FaSpinner className="animate-spin text-sm" />
                          Identifying Face...
                        </>
                      ) : (
                        <>
                          <FaFingerprint className="text-sm" />
                          Scan & Verify Face
                        </>
                      )}
                    </button>
                  )}
                </div>

              </div>
            </div>

          </div>

          {/* Dynamic Daily Attendance Status Grid (Only show if a candidate is selected) */}
          {selectedCandidate && (
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-200 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b pb-3 border-gray-150 gap-2">
                <div>
                  <h3 className="font-bold text-gray-800 text-base flex items-center gap-2">
                    <FaCalendarCheck className="text-[#2BB5CE]" />
                    Daily Attendance Log Checklist
                  </h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Verify candidate once daily. Required verifications is tied to course duration ({courseDurationDays} Days).
                  </p>
                </div>
                
                <div className="px-3 py-1 bg-cyan-50 text-cyan-700 text-xs font-semibold rounded-full border border-cyan-100 w-fit">
                  Verified: {attendanceRecords.length} / {courseDurationDays} Days
                </div>
              </div>

              {/* Days Grid */}
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-3 max-h-[220px] overflow-y-auto pr-1">
                {Array.from({ length: courseDurationDays }, (_, i) => {
                  const dayNum = i + 1;
                  const record = attendanceRecords.find(r => (typeof r === 'number' ? r === dayNum : r?.day === dayNum));
                  const isChecked = !!record;
                  const isTarget = targetDayToVerify === dayNum;
                  const isLocked = dayNum > targetDayToVerify;

                  return (
                    <div
                      key={dayNum}
                      onClick={() => {
                        if (!isLocked && !isChecked) {
                          setTargetDayToVerify(dayNum);
                        }
                      }}
                      className={`flex flex-col items-center justify-center p-2.5 rounded-xl border text-center transition-all select-none min-h-[70px]
                        ${isChecked 
                          ? "bg-emerald-50 border-emerald-200 text-emerald-800 font-bold" 
                          : isTarget
                            ? "bg-cyan-50 border-cyan-300 text-cyan-800 font-extrabold ring-2 ring-cyan-200 cursor-pointer"
                            : isLocked
                              ? "bg-gray-50 border-gray-150 text-gray-300 cursor-not-allowed"
                              : "bg-white border-gray-200 text-gray-600 hover:border-cyan-300 cursor-pointer hover:bg-cyan-50/20"
                        }`}
                    >
                      <span className="text-[10px] uppercase tracking-wider text-gray-400 block mb-1">
                        Day {dayNum}
                      </span>

                      {isChecked ? (
                        <div className="flex flex-col items-center">
                          <FaCheckCircle className="text-emerald-500 text-sm animate-pulse" />
                          {record && record.verifiedAt && (role === "admin" || role === "superAdmin") && (
                            <span className="text-[8px] text-gray-500 font-semibold leading-tight mt-1.5 block select-text">
                              {new Date(record.verifiedAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                              <br/>
                              {new Date(record.verifiedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                      ) : isTarget ? (
                        <span className="text-[9px] font-bold text-cyan-600 animate-pulse bg-cyan-100/80 px-1.5 py-0.5 rounded-full uppercase">
                          Verify
                        </span>
                      ) : (
                        <FaLock className="text-gray-300 text-xs" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default FaceVerification;
